import api from './api';
import { TaskAnalytics } from '../types/task';
import { CalendarEvent } from './calendar';
import { performOptimisticUpdate } from '../utils/optimisticUpdate';

// Task types
export interface Task {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  status: 'todo' | 'in_progress' | 'completed';
  priority: 'low' | 'medium' | 'high' | number; // Support both string and integer (1-5) priorities
  due_date?: string;
  project_id?: string;
  email_account_id?: string;
  email_account?: {
    id: string;
    email_address: string;
    provider: string;
  };
  contact_id?: string;
  contact?: {
    id: string;
    name: string;
    email: string;
    company?: string;
  };
  source: 'manual' | 'ai_extracted';
  source_email_id?: string;
  source_email?: {
    id: string;
    subject: string;
    sender_email: string;
    sender_name?: string;
    received_at?: string;
  };
  goal_ids?: string[];
  goals?: Array<{ id: string; title: string }>;
  ai_confidence_score?: number;
  ai_extraction_metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  // Time blocking fields
  estimated_duration?: number; // Duration in minutes
  calendar_event_id?: string;
  calendar_event?: CalendarEvent; // Nested event if scheduled
  // Dependency fields
  blocking_tasks?: Array<{id: string; title: string; status: string}>;
  blocked_tasks?: Array<{id: string; title: string; status: string}>;
  is_blocked?: boolean;
  // Endeavor board
  endeavor_id?: string | null;
  position_x?: number | null;
  position_y?: number | null;
  endeavor_sort?: number | null;
}

export interface Project {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  color?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateTaskRequest {
  title: string;
  description?: string;
  status?: 'todo' | 'in_progress' | 'completed';
  priority?: number; // Integer 1-5 (1 is highest priority)
  due_date?: string;
  project_id?: string;
  contact_id?: string;
  email_account_id?: string; // Optional account assignment
  source_email_id?: string; // Link to the email this task was created from
  estimated_duration?: number; // Duration in minutes
}

export interface UpdateTaskRequest {
  title?: string;
  description?: string;
  status?: 'todo' | 'in_progress' | 'completed';
  priority?: number; // Integer 1-5 (1 is highest priority)
  due_date?: string | null;  // null to clear the due date
  project_id?: string;
  contact_id?: string | null;  // null to unlink
  email_account_id?: string | null;  // null to unlink from email account
  estimated_duration?: number; // Duration in minutes
}

export interface CreateProjectRequest {
  name: string;
  description?: string;
  color?: string;
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string;
  color?: string;
}

export interface ExtractedTask {
  title: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high';
  due_date?: string;
}

export interface ExtractTasksResponse {
  tasks: ExtractedTask[];
  email_id: string;
}

export interface TaskFilters {
  status?: 'todo' | 'in_progress' | 'completed';
  priority?: 'low' | 'medium' | 'high';
  project_id?: string;
  email_account_id?: string | null;  // null = unified view, string = specific account
  account_scope?: 'unified' | 'unassigned';  // Explicit scope filter
  include_no_account?: boolean;
  exclude_completed?: boolean;  // Drop completed tasks server-side (mirrors sidebar 'active')
  force_refresh?: boolean;  // Skip backend cache and return fresh data
}

export interface TaskCounts {
  total_unfinished: number;
  overdue: number;
}

// Task API methods
export const getTasks = async (filters?: TaskFilters): Promise<Task[]> => {
  const params = new URLSearchParams();

  // Fetch more tasks per page (default is 50, max is 100 on backend - need to update backend too)
  params.append('per_page', '200');

  if (filters?.status) {
    params.append('status', filters.status);
  }
  if (filters?.priority) {
    params.append('priority', filters.priority);
  }
  if (filters?.project_id) {
    params.append('project_id', filters.project_id);
  }
  // Add account scope filter (takes priority over email_account_id)
  if (filters?.account_scope) {
    params.append('account_scope', filters.account_scope);
  } else if (filters?.email_account_id) {
    // Fallback to email_account_id for backward compatibility
    params.append('email_account_id', filters.email_account_id);
  }
  if (filters?.include_no_account !== undefined) {
    params.append('include_no_account', String(filters.include_no_account));
  }
  if (filters?.exclude_completed) {
    params.append('exclude_completed', 'true');
  }
  if (filters?.force_refresh) {
    params.append('force_refresh', 'true');
  }

  const url = params.toString() ? `/api/v1/tasks?${params.toString()}` : '/api/v1/tasks';
  const response = await api.get<{ tasks: Task[]; pagination: Record<string, unknown> }>(url);
  return response.data.tasks;
};

export const getTaskCounts = async (accountId?: string | null): Promise<TaskCounts> => {
  const params = new URLSearchParams();

  if (accountId === 'unassigned') {
    params.append('account_scope', 'unassigned');
  } else if (accountId === null || accountId === undefined) {
    params.append('account_scope', 'unified');
  } else {
    params.append('email_account_id', accountId);
  }

  const url = params.toString() ? `/api/v1/tasks/counts?${params.toString()}` : '/api/v1/tasks/counts';
  const response = await api.get<TaskCounts>(url);
  return response.data;
};

/**
 * Get count of active tasks (status != 'completed') for nav badge.
 * This matches the frontend's "Active" filter exactly.
 * @param accountId - Optional account ID filter ('all' or omit for all accounts)
 */
export const getActiveTasksCount = async (accountId?: string | null): Promise<number> => {
  const params = new URLSearchParams();

  if (accountId && accountId !== 'all') {
    params.append('email_account_id', accountId);
  }

  const url = params.toString()
    ? `/api/v1/tasks/active-count?${params.toString()}`
    : '/api/v1/tasks/active-count';
  const response = await api.get<{ count: number }>(url);
  return response.data.count;
};

export const getTask = async (id: string): Promise<Task> => {
  const response = await api.get<Task>(`/api/v1/tasks/${id}`);
  return response.data;
};

export const createTask = async (task: CreateTaskRequest): Promise<Task> => {
  const response = await api.post<Task>('/api/v1/tasks', task);
  return response.data;
};

export const updateTask = async (id: string, task: UpdateTaskRequest): Promise<Task> => {
  const response = await api.put<Task>(`/api/v1/tasks/${id}`, task);
  return response.data;
};

export const deleteTask = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/tasks/${id}`);
};

export const completeTask = async (id: string): Promise<Task> => {
  const response = await api.post<Task>(`/api/v1/tasks/${id}/complete`);
  return response.data;
};

export const extractTasksFromEmail = async (emailId: string): Promise<ExtractTasksResponse> => {
  const response = await api.post<ExtractTasksResponse>(`/api/v1/tasks/extract-from-email/${emailId}`);
  return response.data;
};

// Project API methods
export const getProjects = async (): Promise<Project[]> => {
  const response = await api.get<{ projects: Project[]; pagination: Record<string, unknown> }>('/api/v1/projects');
  return response.data.projects;
};

export const getProject = async (id: string): Promise<Project> => {
  const response = await api.get<Project>(`/api/v1/projects/${id}`);
  return response.data;
};

export const createProject = async (project: CreateProjectRequest): Promise<Project> => {
  const response = await api.post<Project>('/api/v1/projects', project);
  return response.data;
};

export const updateProject = async (id: string, project: UpdateProjectRequest): Promise<Project> => {
  const response = await api.put<Project>(`/api/v1/projects/${id}`, project);
  return response.data;
};

export const deleteProject = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/projects/${id}`);
};

// Task Insights types
export interface TaskStats {
  overdue: number;
  due_today: number;
  due_soon: number;
  open_total: number;
  completed_last_7_days: number;
}

export interface BurndownDataPoint {
  date: string;
  open_count: number;
}

export interface TaskInsights {
  stats: TaskStats;
  burndown: BurndownDataPoint[];
}

// Task Insights API
export const getTaskInsights = async (filters?: { email_account_id?: string; account_scope?: string }): Promise<TaskInsights> => {
  const params = new URLSearchParams();
  if (filters?.email_account_id) {
    params.append('email_account_id', filters.email_account_id);
  }
  if (filters?.account_scope) {
    params.append('account_scope', filters.account_scope);
  }

  const response = await api.get<TaskInsights>(`/api/v1/tasks/insights?${params.toString()}`);
  return response.data;
};

// Bulk action types
export interface BulkTaskActionResponse {
  success_count: number;
  failed_count: number;
  errors?: string[];
}

/**
 * Bulk complete tasks
 * @param taskIds - Array of task IDs to complete
 */
export const bulkCompleteTasks = async (taskIds: string[]): Promise<BulkTaskActionResponse> => {
  const response = await api.post<BulkTaskActionResponse>('/api/v1/tasks/bulk-complete', {
    task_ids: taskIds,
  });
  return response.data;
};

/**
 * Bulk delete tasks
 * @param taskIds - Array of task IDs to delete
 */
export const bulkDeleteTasks = async (taskIds: string[]): Promise<BulkTaskActionResponse> => {
  const response = await api.post<BulkTaskActionResponse>('/api/v1/tasks/bulk-delete', {
    task_ids: taskIds,
  });
  return response.data;
};

/**
 * Bulk update task due dates
 * @param taskIds - Array of task IDs to update
 * @param dueDate - New due date (ISO string)
 */
export const bulkUpdateTaskDueDate = async (taskIds: string[], dueDate: string | null): Promise<BulkTaskActionResponse> => {
  const response = await api.post<BulkTaskActionResponse>('/api/v1/tasks/bulk-update-due-date', {
    task_ids: taskIds,
    due_date: dueDate,
  });
  return response.data;
};

/**
 * Bulk assign tasks to goals
 * @param taskIds - Array of task IDs to assign
 * @param goalIds - Array of goal IDs to assign to
 */
export const bulkAssignToGoals = async (taskIds: string[], goalIds: string[]): Promise<BulkTaskActionResponse> => {
  const response = await api.post<BulkTaskActionResponse>('/api/v1/tasks/bulk-assign-goals', {
    task_ids: taskIds,
    goal_ids: goalIds,
  });
  return response.data;
};

/**
 * Get task analytics (burndown, gantt, metrics)
 * @param params - Query parameters for analytics
 */
export const getTaskAnalytics = async (params: {
  projectId?: string;
  startDate: string;
  endDate: string;
  groupBy?: string;
}): Promise<TaskAnalytics> => {
  const queryParams = new URLSearchParams();
  queryParams.append('start_date', params.startDate);
  queryParams.append('end_date', params.endDate);

  if (params.projectId) {
    queryParams.append('project_id', params.projectId);
  }

  if (params.groupBy) {
    queryParams.append('group_by', params.groupBy);
  }

  const response = await api.get<TaskAnalytics>(`/api/v1/tasks/analytics?${queryParams.toString()}`);
  return response.data;
};

// Time blocking types and API
export interface ScheduleTaskRequest {
  start_time: string; // ISO datetime string
  duration_minutes?: number; // Optional, uses task.estimated_duration if not provided
  calendar_id?: string; // Optional, defaults to primary calendar
}

export interface ScheduleTaskResponse {
  event: CalendarEvent;
  conflicts: CalendarEvent[];
}

/**
 * Schedule a task on the calendar by creating a time block event
 * @param taskId - Task ID to schedule
 * @param startTime - Start time as ISO string
 * @param durationMinutes - Optional duration in minutes (uses task.estimated_duration if not provided)
 * @param calendarId - Optional calendar ID (defaults to primary calendar)
 */
export const scheduleTask = async (
  taskId: string,
  startTime: string,
  durationMinutes?: number,
  calendarId?: string
): Promise<ScheduleTaskResponse> => {
  const requestData: ScheduleTaskRequest = {
    start_time: startTime,
    duration_minutes: durationMinutes,
    calendar_id: calendarId,
  };

  const response = await api.post<ScheduleTaskResponse>(
    `/api/v1/tasks/${taskId}/schedule`,
    requestData
  );
  return response.data;
};

/**
 * Remove a task from the calendar (unschedule)
 * @param taskId - Task ID to unschedule
 */
export const unscheduleTask = async (taskId: string): Promise<void> => {
  await api.delete(`/api/v1/tasks/${taskId}/schedule`);
};

// Task Dependency API methods
/**
 * Add a dependency to a task
 * @param taskId - The dependent task (blocked task)
 * @param dependsOnTaskId - The blocking task (must complete first)
 * @param dependencyType - Type of dependency (defaults to 'finish_to_start')
 */
export const addTaskDependency = async (
  taskId: string,
  dependsOnTaskId: string,
  dependencyType: 'finish_to_start' | 'start_to_start' | 'finish_to_finish' = 'finish_to_start'
): Promise<Task> => {
  const response = await api.post<Task>(`/api/v1/tasks/${taskId}/dependencies`, {
    depends_on_task_id: dependsOnTaskId,
    dependency_type: dependencyType,
  });
  return response.data;
};

/**
 * Remove a dependency from a task
 * @param taskId - The dependent task
 * @param dependsOnTaskId - The blocking task to remove
 */
export const removeTaskDependency = async (
  taskId: string,
  dependsOnTaskId: string
): Promise<Task> => {
  const response = await api.delete<Task>(`/api/v1/tasks/${taskId}/dependencies/${dependsOnTaskId}`);
  return response.data;
};

/**
 * Validate a dependency (check for circular dependencies)
 * @param taskId - The dependent task
 * @param dependsOnTaskId - The blocking task to validate
 */
export const validateDependency = async (
  taskId: string,
  dependsOnTaskId: string
): Promise<{valid: boolean; error?: string}> => {
  const response = await api.post<{valid: boolean; error?: string}>(
    `/api/v1/tasks/${taskId}/dependencies/validate`,
    { depends_on_task_id: dependsOnTaskId }
  );
  return response.data;
};

/**
 * Get critical path for tasks
 * @param projectId - Optional project ID to filter tasks
 */
export const getCriticalPath = async (
  projectId?: string
): Promise<import('../types/task').CriticalPath> => {
  const params = new URLSearchParams();
  if (projectId) {
    params.append('project_id', projectId);
  }

  const url = params.toString()
    ? `/api/v1/tasks/critical-path?${params.toString()}`
    : '/api/v1/tasks/critical-path';
  const response = await api.get<import('../types/task').CriticalPath>(url);
  return response.data;
};

/**
 * Optimistic update wrappers for task actions
 * These provide instant UI feedback with automatic error handling
 */

/**
 * Update task with optimistic update
 *
 * @param taskId - Task ID to update
 * @param updates - Task updates to apply
 * @param onOptimisticUpdate - Callback to update local state immediately
 * @param onRevert - Callback to revert local state on error
 * @returns Promise that resolves when API call completes
 */
export const updateTaskOptimistic = async (
  taskId: string,
  updates: UpdateTaskRequest,
  onOptimisticUpdate: () => void,
  onRevert: () => void
): Promise<Task> => {
  let result: Task | undefined;

  await performOptimisticUpdate({
    optimisticUpdate: onOptimisticUpdate,
    revertUpdate: onRevert,
    apiCall: async () => {
      result = await updateTask(taskId, updates);
      return result;
    },
    successMessage: getUpdateMessage(updates),
    errorMessage: 'Failed to update task',
    onSuccess: (updatedTask) => {
      result = updatedTask;
    },
  });

  return result!;
};

/**
 * Delete task with optimistic update
 *
 * @param taskId - Task ID to delete
 * @param onOptimisticUpdate - Callback to update local state (remove from list)
 * @param onRevert - Callback to revert local state (restore to list)
 * @returns Promise that resolves when API call completes
 */
export const deleteTaskOptimistic = async (
  taskId: string,
  onOptimisticUpdate: () => void,
  onRevert: () => void
): Promise<void> => {
  await performOptimisticUpdate({
    optimisticUpdate: onOptimisticUpdate,
    revertUpdate: onRevert,
    apiCall: () => deleteTask(taskId),
    successMessage: 'Task deleted',
    errorMessage: 'Failed to delete task',
  });
};

/**
 * Helper function to generate update message based on what was updated
 */
function getUpdateMessage(updates: UpdateTaskRequest): string {
  if (updates.status !== undefined) {
    if (updates.status === 'completed') return 'Task completed';
    if (updates.status === 'in_progress') return 'Task in progress';
    if (updates.status === 'todo') return 'Task reopened';
  }
  if (updates.priority !== undefined) return 'Priority updated';
  if (updates.due_date !== undefined) return 'Due date updated';
  if (updates.title !== undefined) return 'Task updated';
  if (updates.project_id !== undefined) return 'Project updated';
  if (updates.email_account_id !== undefined) return 'Account updated';
  return 'Task updated';
}
